import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import VariableInserter from '@/components/email-center/VariableInserter';
import { HighlightedInput, HighlightedTextarea } from '@/components/email-center/HighlightedInputs';
import type { EmailTemplate, EmailTemplateFormData } from '../types';

interface TemplateEditorSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingTemplate: EmailTemplate | null;
  formData: EmailTemplateFormData;
  onFormDataChange: (data: EmailTemplateFormData) => void;
  onSave: () => void;
  isSaving: boolean;
}

export const TemplateEditorSheet: React.FC<TemplateEditorSheetProps> = ({
  isOpen,
  onOpenChange,
  editingTemplate,
  formData,
  onFormDataChange,
  onSave,
  isSaving,
}) => {
  const insertVariableIntoSubject = (variable: string) => {
    const input = document.getElementById('templateSubject') as HTMLInputElement;
    if (input) {
      const cursorPos = input.selectionStart || 0;
      const textBefore = formData.subject.substring(0, cursorPos);
      const textAfter = formData.subject.substring(cursorPos);
      const newText = textBefore + `{{${variable}}}` + textAfter;

      onFormDataChange({ ...formData, subject: newText });

      setTimeout(() => {
        input.focus();
        const newCursorPos = cursorPos + variable.length + 4;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const insertVariableIntoBody = (variable: string) => {
    const textarea = document.getElementById('templateBody') as HTMLTextAreaElement;
    if (textarea) {
      const cursorPos = textarea.selectionStart || 0;
      const textBefore = formData.body.substring(0, cursorPos);
      const textAfter = formData.body.substring(cursorPos);
      const newText = textBefore + `{{${variable}}}` + textAfter;

      onFormDataChange({ ...formData, body: newText });

      setTimeout(() => {
        textarea.focus();
        const newCursorPos = cursorPos + variable.length + 4;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        className="sm:max-w-2xl w-full overflow-y-auto flex flex-col justify-between"
        side="right"
      >
        <div className="flex-grow">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-xl font-bold text-gray-900 text-left">
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </SheetTitle>
            <SheetDescription className="text-left text-gray-600">
              {editingTemplate
                ? 'Update your email template'
                : 'Create a reusable email template with variables'}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="templateName"
                className="text-xs font-bold text-gray-500 uppercase tracking-wider"
              >
                Template Name
              </Label>
              <Input
                id="templateName"
                value={formData.name}
                onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
                placeholder="e.g., Welcome Email, Follow-up, etc."
                className="border-gray-200 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:border-transparent rounded-xl h-11"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <Label
                  htmlFor="templateSubject"
                  className="text-xs font-bold text-gray-500 uppercase tracking-wider"
                >
                  Subject
                </Label>
                <VariableInserter
                  onInsert={insertVariableIntoSubject}
                  buttonText="Insert Variable"
                  showChevron={false}
                />
              </div>
              <HighlightedInput
                id="templateSubject"
                value={formData.subject}
                onChange={(e) => onFormDataChange({ ...formData, subject: e.target.value })}
                placeholder="Email subject line..."
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <Label
                  htmlFor="templateBody"
                  className="text-xs font-bold text-gray-500 uppercase tracking-wider"
                >
                  Email Body
                </Label>
                <VariableInserter
                  onInsert={insertVariableIntoBody}
                  buttonText="Insert Variable"
                  showChevron={false}
                />
              </div>
              <HighlightedTextarea
                id="templateBody"
                value={formData.body}
                onChange={(e) => onFormDataChange({ ...formData, body: e.target.value })}
                placeholder="Email content... Use {{variable}} for personalization."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-8">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl border-gray-300 text-gray-700 font-medium px-5 py-2.5"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="bg-[#A655F8] hover:bg-[#9333EA] text-white font-medium rounded-xl px-5 py-2.5 shadow-sm transition-colors"
          >
            {editingTemplate ? 'Update Template' : 'Create Template'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
